import type { RuntimeConfig } from "../../types";
import { decodeJwtPayload } from "./jwt";
import type { StoredTokens } from "./session";

const SCOPE = "openid email";

export class AuthError extends Error {}

function activeApplicationUrl(config: RuntimeConfig): string {
  const applicationUrls = config.applicationUrls;
  const currentOrigin = globalThis.location?.origin;
  if (!currentOrigin) {
    return applicationUrls[0]!;
  }
  const configuredOrigin = applicationUrls.find((url) => url === currentOrigin);
  if (!configuredOrigin) {
    throw new AuthError("This website address is not configured for sign-in.");
  }
  return configuredOrigin;
}

/** Cognito Hosted UI / managed-login authorize endpoint, authorization-code + PKCE, no secret. */
export function buildAuthorizeUrl(
  config: RuntimeConfig,
  params: { state: string; codeChallenge: string; screenHint?: "signup" },
): string {
  const url = new URL("/oauth2/authorize", config.cognitoDomain);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.userPoolClientId);
  url.searchParams.set("redirect_uri", new URL("/owner", activeApplicationUrl(config)).toString());
  url.searchParams.set("scope", SCOPE);
  url.searchParams.set("code_challenge", params.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", params.state);
  if (params.screenHint) url.searchParams.set("screen_hint", params.screenHint);
  return url.toString();
}

export function buildLogoutUrl(config: RuntimeConfig): string {
  const url = new URL("/logout", config.cognitoDomain);
  url.searchParams.set("client_id", config.userPoolClientId);
  url.searchParams.set("logout_uri", new URL("/", activeApplicationUrl(config)).toString());
  return url.toString();
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
}

/** Exchanges an authorization code for tokens. Public client: the PKCE code
 * verifier substitutes for a client secret, so none is sent. */
export async function exchangeCodeForTokens(
  config: RuntimeConfig,
  params: { code: string; verifier: string },
): Promise<StoredTokens> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.userPoolClientId,
    code: params.code,
    redirect_uri: new URL("/owner", activeApplicationUrl(config)).toString(),
    code_verifier: params.verifier,
  });

  let res: Response;
  try {
    res = await fetch(new URL("/oauth2/token", config.cognitoDomain), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
  } catch {
    throw new AuthError("Couldn't reach the sign-in service. Check your connection and try again.");
  }

  if (!res.ok) {
    throw new AuthError("Sign-in didn't complete. The link may have expired — try signing in again.");
  }

  const data = (await res.json()) as TokenResponse;
  const payload = decodeJwtPayload(data.id_token);
  const email = typeof payload?.email === "string" ? payload.email : null;

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    email,
  };
}
